import { SubmitHandler } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { DATE_MASK_FORMAT } from '@givebutter/util';
import * as Collapsible from '@radix-ui/react-collapsible';
import { updateTransaction } from 'api/transactions';
import * as EditableInput from 'components/editable-input';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentTransactionOrById } from 'customHooks/useCurrentTransactionOrById';
import { useForceRender } from 'customHooks/useForceRender';
import { maskedDateToServerDateTime, rawDateFromServer } from 'helpers/dates';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';
import { transactionQueryKeys } from 'queries/transaction-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface DonationDateForm {
    donationDate: string;
}

const TransactionEditableDonationDate = () => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const { accountId, timezone } = useCurrentAccount();
    const { key, forceRender } = useForceRender();
    const { data: transaction } = useCurrentTransactionOrById();
    const methods = Form.useForm<DonationDateForm>({
        defaultValues: {
            donationDate: transaction?.transacted_at
                ? rawDateFromServer(transaction.transacted_at)
                      .setZone(timezone)
                      .toFormat(DATE_MASK_FORMAT)
                : '',
        },
    });
    const { control, path, handleSubmit, resetFormButton } = methods;

    const { mutate: submit } = useMutation({
        mutationFn: (values: DonationDateForm) =>
            updateTransaction(transaction?.id!, {
                transacted_at: maskedDateToServerDateTime(values.donationDate),
            }),
        onSuccess: () => {
            toast.success('Successfully updated transaction check deposit date!');
            queryClient.invalidateQueries({
                queryKey: transactionQueryKeys.transactions(accountId),
            });

            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.current(accountId),
            });

            forceRender();
        },
        onError: (e: GivebutterAxiosError) => {
            toast.error(
                e.response?.data?.message ?? 'Failed to update transaction check deposit date',
            );
            resetFormButton();
        },
    });

    return (
        <Form.Provider key={key} {...methods}>
            <form
                className={'contents'}
                onSubmit={handleSubmit(submit as SubmitHandler<DonationDateForm>)}
            >
                <div className={'flex flex-col'}>
                    <Form.Label name={path('donationDate')} className={'text-gray-800'}>
                        Donation date
                        <Form.LabelAsterisk />
                    </Form.Label>
                    <EditableInput.Root>
                        <EditableInput.ReadOnly>
                            <div className={'flex items-center gap-2'}>
                                {transaction?.transacted_at ? (
                                    <>
                                        <p className={'text-gray-600'}>
                                            {rawDateFromServer(transaction.transacted_at)
                                                .setZone(timezone)
                                                .toLocaleString(DateTime.DATE_FULL)}
                                        </p>
                                        <EditableInput.ReadOnlyEditTrigger />
                                    </>
                                ) : (
                                    <Collapsible.Trigger className={'font-medium text-blue-500'}>
                                        Add +
                                    </Collapsible.Trigger>
                                )}
                            </div>
                        </EditableInput.ReadOnly>
                        <EditableInput.Content>
                            <Form.Container>
                                <Form.TextInputRoot
                                    control={control}
                                    name={'donationDate'}
                                    rules={{ required: 'Please enter a donation date' }}
                                    inputRootProps={{
                                        mask: 'date',
                                    }}
                                >
                                    <Form.TextInputForm placeholder={'mm/dd/yyyy'} />
                                </Form.TextInputRoot>
                                <Form.Error name={path('donationDate')} />
                            </Form.Container>
                            <EditableInput.ActionsContainer>
                                <EditableInput.Cancel />
                                <Form.SubmitButton>Save</Form.SubmitButton>
                            </EditableInput.ActionsContainer>
                        </EditableInput.Content>
                    </EditableInput.Root>
                </div>
            </form>
        </Form.Provider>
    );
};

export { TransactionEditableDonationDate };
